/** CanalesTab — CRUD de canales (Correo, Teléfono, Web, WhatsApp, Presencial...). */
import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../../components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';
import { useAbortableLoad, isCanceled } from '../../../hooks/useAbortableLoad';

const empty = { codigo: '', nombre: '', icono: '', color: '#3B82F6', activo: true, orden: 0 };

export default function CanalesTab({ api }) {
  const { user } = useAuth();
  const isAdmin = ['ADMINISTRADOR', 'SUPERADMIN'].includes(user?.rol);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async (signal) => {
    setLoading(true);
    try {
      const { data } = await api.get('/tickets/canales', { signal });
      setItems(data);
    } catch (e) {
      if (isCanceled(e)) return;
      toast.error('Error cargando canales');
    } finally { setLoading(false); }
  };

  useAbortableLoad((signal) => load(signal), []);

  useAbortableLoad((signal) => load(signal), []);

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setDialogOpen(true); };
  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar el canal "${it.nombre}"?`)) return;
    try {
      await api.delete(`/tickets/canales/${it.id}`);
      toast.success('Canal eliminado');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error eliminando'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, orden: parseInt(form.orden || 0, 10) };
      if (editing) {
        await api.put(`/tickets/canales/${editing.id}`, payload);
        toast.success('Canal actualizado');
      } else {
        await api.post('/tickets/canales', payload);
        toast.success('Canal creado');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">Canales por los que los clientes pueden registrar tickets.</p>
        {isAdmin && (
          <Button onClick={openCreate} size="sm" data-testid="btn-nuevo-canal">
            <Plus className="w-4 h-4 mr-1" /> Nuevo canal
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="border rounded-md divide-y">
          {items.length === 0 && <div className="px-4 py-6 text-sm text-slate-500 text-center">Sin canales</div>}
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-3 px-4 py-3" data-testid={`canal-row-${it.codigo}`}>
              <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: it.color || '#cbd5e1' }} />
              <div className="flex-1">
                <div className="text-sm font-medium">{it.nombre}</div>
                <div className="text-xs text-slate-500 font-mono">{it.codigo}</div>
              </div>
              <Badge variant={it.activo ? 'default' : 'secondary'}>{it.activo ? 'Activo' : 'Inactivo'}</Badge>
              {isAdmin && (
                <>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-canal-${it.codigo}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(it)} data-testid={`del-canal-${it.codigo}`}>
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Editar canal' : 'Nuevo canal'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Código</Label>
              <Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="CORREO" disabled={!!editing} className="font-mono uppercase" />
            </div>
            <div>
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Correo" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Icono (lucide-react)</Label>
                <Input value={form.icono || ''} onChange={(e) => setForm({ ...form, icono: e.target.value })} placeholder="Mail" />
              </div>
              <div>
                <Label>Color</Label>
                <Input type="color" value={form.color || '#3B82F6'} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 p-1" />
              </div>
              <div>
                <Label>Orden</Label>
                <Input type="number" value={form.orden} onChange={(e) => setForm({ ...form, orden: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} />
              <Label>Activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-guardar-canal">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
